import {FC, useState} from 'react'
import {Dropdown, Nav} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {BoxArrowRight} from 'react-bootstrap-icons'
import {useAuth} from '../../hooks/useAuth'
import {useTranslation} from 'react-i18next'
import MegaMenu from './MegaMenu'
import {getNavigationItems, filterNavByFeatureFlags} from './utils/roleHierarchy'
import {getUserDisplayName} from '../../utils/userUtils'
import {Logo, ProfileAvatar} from '../ui'
import WeatherWarningButton from '../weather/WeatherWarningButton'
import {canManageWeather} from '../weather/weatherAccess'
import type {User} from '../../types/auth'
import buildInfo from '../../buildInfo.json'

interface DesktopNavigationProps {
    user: User
    /** Whether to use tap instead of hover for megamenu (tablet landscape) */
    useTapTrigger?: boolean
}

const DesktopNavigation: FC<DesktopNavigationProps> = ({user, useTapTrigger = false}) => {
    const navigate = useNavigate()
    const {logout} = useAuth()
    const {t} = useTranslation('common')
    const [dropdownOpen, setDropdownOpen] = useState(false)

    const handleLogout = async (): Promise<void> => {
        await logout()
        navigate('/login')
    }

    const handleProfileClick = (): void => {
        setDropdownOpen(false)
        navigate('/profile')
    }

    const handleDashboardClick = (e: React.MouseEvent): void => {
        e.preventDefault()
        navigate('/dashboard')
    }

    const primaryRole = user?.roles?.[0] || 'PLAYER'
    const roleDisplay = t(`roles.display_names.${primaryRole}`)
    const userDisplayName = getUserDisplayName(user)
    const navigationItems = filterNavByFeatureFlags(getNavigationItems(user?.roles || []), user)

    const showWeatherButton = canManageWeather(user)

    return (
        <div className="d-flex align-items-center justify-content-between w-100">
            <a
                href="/dashboard"
                className="d-flex align-items-center text-decoration-none mx-3"
                onClick={handleDashboardClick}
            >
                <Logo size="small"/>
            </a>

            <div className="flex-grow-1 d-flex justify-content-center">
                <MegaMenu
                    items={navigationItems}
                    triggerMode={useTapTrigger ? 'click' : 'hover'}
                />
            </div>

            <Nav className="ms-4 align-items-center">
                {showWeatherButton && (
                    <WeatherWarningButton tenantId={user.tenantId as string} />
                )}
                <Dropdown
                    show={dropdownOpen}
                    onToggle={setDropdownOpen}
                    align="end"
                    style={{ position: 'relative' }}
                >
                    <Dropdown.Toggle
                        variant="link"
                        className="text-decoration-none d-flex align-items-center"
                        id="user-dropdown"
                        data-testid="user-dropdown"
                        style={{color: '#FAFAFA'}}
                    >
                        <ProfileAvatar profileImageUrl={user?.profileImageUrl as string | undefined}
                                       userName={userDisplayName} size={28}
                                       className="me-2 text-light"/>
                        <span className="me-1">{userDisplayName}</span>
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="shadow-sm" style={{ position: 'absolute' }}>
                        <Dropdown.Header>
                            <div className="fw-semibold">{userDisplayName}</div>
                            <small className="text-muted d-block">{user?.email}</small>
                            <small className="text-muted">{roleDisplay}</small>
                            <small className="text-muted d-block">v{buildInfo.version}</small>
                        </Dropdown.Header>

                        <Dropdown.Item onClick={handleProfileClick} className="d-flex align-items-center">
                            <ProfileAvatar profileImageUrl={user?.profileImageUrl as string | undefined}
                                           userName={userDisplayName} size={18}
                                           className="me-2 text-dark"/>
                            <span>{t("navigation.roles.PLAYER.items.profile")}</span>
                        </Dropdown.Item>

                        <Dropdown.Divider/>

                        <Dropdown.Item onClick={handleLogout} className="d-flex align-items-center"
                                       data-testid="logout-button">
                            <BoxArrowRight size={18} className="me-2"/>
                            <span>{t("navigation.actions.logout")}</span>
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </Nav>
        </div>
    )
}

export default DesktopNavigation
